import SiteImage from "@/components/SiteImage";
import DeferredPublicSection, { useInitialHomeDeferral } from "@/components/DeferredPublicSection";
import { CircleScribble } from "@/components/CircledLink";
import { CircleWord, ScribbleUnder } from "@/components/ScribbleAccents";
import React from "react";
import { Link, useLocation } from "wouter";
import Reveal from "@/components/Reveal";
import { usePublicUser as useUser } from "@/lib/publicAuth";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import TrustBadge from "@/components/TrustBadge";
import VideoReviewsSection from "@/components/DeferredVideoReviews";
import { publicAsset } from "@/lib/publicAssets";
import { StoryReview } from "@/components/ReviewsCarousel";
import { HOME_STORY_REVIEWS, WLD_STORY_REVIEWS } from "@/data/reviews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Loader2 } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { ROUTE_META } from "@/lib/routeMeta";
import ResponsiveBackground from "@/components/ResponsiveBackground";
import { trackEvent } from "@/lib/analytics";
import { getSignupToken } from "@/lib/signupChallenge";
import "./home-hero.css";

const BASE_URL = import.meta.env.BASE_URL;
const ASSETS = `${BASE_URL}site/storybrand`;

const FadeUp = Reveal;

export default function SiteHome() {
  usePageMeta(ROUTE_META["/"]);
  const deferSections = useInitialHomeDeferral();
  const [, setLocation] = useLocation();
  const { isSignedIn } = useUser();
  const [visitedFaqs, setVisitedFaqs] = React.useState<Set<string>>(() => new Set());

  const [emailStatus, setEmailStatus] = React.useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    if (!email) return;
    setEmailStatus("submitting");
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, formToken: await getSignupToken(), source: "home" }),
      });
      if (!res.ok) throw new Error("Subscribe failed");
      setEmailStatus("success");
      trackEvent("newsletter_signup_success", { source: "home" });
    } catch {
      setEmailStatus("error");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white font-sans text-[#333333]">
      <SiteNav isSignedIn={!!isSignedIn} setLocation={setLocation} />
      <main style={{ display: "contents" }}>

      {/* HERO — proof [0] dark */}
      <section className="rh-section relative bg-[#141414]">
        <ResponsiveBackground
          desktopSrc={`${BASE_URL}site/home-hero-1440.webp`}
          desktopSrcSet={`${BASE_URL}site/home-hero-1440.webp 1440w, ${BASE_URL}site/home-hero-1920.webp 1920w`}
          mobileSrc={`${BASE_URL}site/home-hero-mobile-804.webp`}
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-[#0F0F0F]/75 hidden md:block" />
        <div className="absolute inset-0 bg-[#0F0F0F]/35 md:hidden" />
        <div className="rh-inner relative max-w-[1240px] mx-auto">
          <FadeUp className="rh-content max-w-[790px] mx-auto text-center">
            <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary rh-eyebrow">
              For youth pastors & leaders in city, suburb & small town
            </div>
            <h1 className="rh-h1 m-0 leading-[0.98] tracking-[-0.03em] font-black text-white text-balance">
              Most curriculum plays it safe. <span className="text-primary">Your students can't afford that.</span>
            </h1>
            <p className="rh-p text-white/75 max-w-[33em] mx-auto text-pretty">
              Reframe gives you conversation-driven, ready-to-teach series that go straight at what students actually face, like mental health, identity, doubt, and culture, so you show up prepared and they build a faith that holds up in the real world.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center rh-ctas">
              <Button
                className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold tracking-[0.09em] uppercase px-[26px] rh-btn rounded-[10px] shadow-[0_12px_30px_rgba(216,36,53,0.3)] transition-transform hover:-translate-y-1 h-auto"
                asChild
              >
                <Link href="/wld">BUY FEATURED CURRICULUM</Link>
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-[2px] border-white text-white hover:bg-white hover:text-[#1A1A1A] font-extrabold tracking-[0.09em] uppercase px-[26px] rh-btn rounded-[10px] transition-colors h-auto"
                asChild
              >
                <Link href="/build-bundle">BUILD YOUR BUNDLE</Link>
              </Button>
            </div>
            <div className="rh-badge flex justify-center" data-testid="hero-trust-badge">
              <div className="rh-badge-box relative">
                <div className="rh-badge-scale absolute left-1/2 top-1/2 origin-center">
                  <TrustBadge />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center rh-quiz">
              <div className="rh-quiz-label font-bold text-white/50 uppercase tracking-widest">
                Not sure where to start?
              </div>
              <Button
                variant="link"
                className="text-primary hover:text-[#B01E2C] font-extrabold text-[14px] tracking-[0.09em] uppercase h-auto p-0"
                asChild
              >
                <Link href="/curriculum-quiz">
                  TAKE THE <CircleScribble>CURRICULUM QUIZ</CircleScribble>
                </Link>
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-[18px] rh-rating font-semibold text-white/60">
              <span className="text-primary tracking-[0.06em]">★★★★★</span>
              <span>4.9 average rating</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>Instant download</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>Built by youth pastors</span>
            </div>
            <FadeUp delay={0.3} className="rh-review max-w-[560px] mx-auto text-center">
              <StoryReview
                review={WLD_STORY_REVIEWS[1]}
                variant="dark"
                context="hero"
                className="border-t-0 pt-0 [&>div]:justify-center [&_blockquote]:text-white [&_figcaption]:text-white/80"
              />
            </FadeUp>
          </FadeUp>
        </div>
      </section>

      <DeferredPublicSection enabled={deferSections} id="home-content">
      {/* MARQUEE */}
      <div className="bg-primary overflow-hidden py-4">
        <div className="flex w-max animate-marquee">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-[30px] pr-[30px] text-[13px] font-extrabold tracking-[0.15em] uppercase text-white whitespace-nowrap">
              <span>13 New Series</span><span className="text-white/70">✧</span>
              <span>Teaching Videos</span><span className="text-white/70">✧</span>
              <span>Intentional Games</span><span className="text-white/70">✧</span>
              <span>YouVersion Devotionals</span><span className="text-white/70">✧</span>
              <span>12-Week Middle School Series</span><span className="text-white/70">✧</span>
              <span>Leader Training</span><span className="text-white/70">✧</span>
            </div>
          ))}
        </div>
      </div>

      {/* PROBLEM */}
      <section className="bg-[#F2F2F3]">
        <div className="max-w-[1240px] mx-auto px-10 pt-[96px] pb-[100px]">
          <div className="grid gap-[40px] lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.72fr)] lg:items-start lg:gap-[72px]">
            <FadeUp className="max-w-[760px]">
              <h2 className="m-0 text-[clamp(32px,3.3vw,48px)] leading-[1.06] tracking-[-0.03em] font-black text-[#1A1A1A] text-balance">
                Finding curriculum that connects shouldn't be another headache.
              </h2>
              <p className="mt-[20px] text-[19px] leading-[1.55] text-[#5a5a5c] text-pretty">
                You didn't get into youth ministry to spend the night before youth group rewriting a lesson that still won't land.
              </p>
            </FadeUp>
            <FadeUp delay={0.12} className="w-full max-w-[520px] lg:justify-self-end">
              <StoryReview review={HOME_STORY_REVIEWS[5]} variant="light" context="editorial" />
            </FadeUp>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] mt-[48px]">
            {[
              "Generic, dated material that ignores what students actually face",
              "Nothing that touches mental health, identity, or culture head-on",
              "Feeling forced to choose between relevant or biblically faithful",
              "Prepping late every week, wondering if it's even reaching them"
            ].map((p, i) => (
              <FadeUp key={i} delay={0.1 * i} className="flex gap-4 items-start bg-white border border-[#1A1A1A]/5 rounded-[16px] p-6">
                <span className="flex-none w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-extrabold flex items-center justify-center mt-0.5">✕</span>
                <span className="text-[16.5px] leading-[1.45] font-semibold text-[#1A1A1A]">{p}</span>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.4}>
            <p className="mt-[44px] mx-auto max-w-[860px] text-center text-[21px] leading-[1.5] font-semibold text-[#1A1A1A] text-pretty">
              Every <CircleWord delay={0.2}>student</CircleWord> is looking for someone they can <ScribbleUnder delay={0.6}>trust</ScribbleUnder> and something that's <ScribbleUnder delay={1.0}>real</ScribbleUnder>, and no <CircleWord delay={1.4}>leader</CircleWord> can choose between <ScribbleUnder delay={1.8}>cultural relevance</ScribbleUnder> and <ScribbleUnder delay={2.2}>biblical truth</ScribbleUnder> while giving it to them.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* VALUE — proof [2] light */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-10 pt-[96px] pb-[100px]">
          <FadeUp className="max-w-[720px] mx-auto text-center">
            <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[18px]">The difference</div>
            <h2 className="m-0 text-[clamp(32px,3.3vw,48px)] leading-[1.06] tracking-[-0.03em] font-black text-[#1A1A1A]">
              Why Reframe is <CircleWord>different</CircleWord>
            </h2>
            <p className="mt-[18px] text-[18.5px] leading-[1.55] text-[#5a5a5c] text-pretty">
              Everything is built around one idea: get students talking, and let the Gospel do the transforming.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-[56px]">
            {[
              { img: "impact1.png", title: "Relevant & faithful", desc: "Students are stuck between culture and faith. We give them a framework to hold both, with no compromise either way." },
              { img: "impact2.png", title: "Conversation-first", desc: "Lead a dialogue, not a monologue. Series designed to get students sharing and connecting with God and each other." },
              { img: "impact3.png", title: "No topic off-limits", desc: "Mental health, identity, sexuality, doubt. If students are talking about it, the church should be too." }
            ].map((v, i) => (
              <FadeUp key={i} delay={0.1 * i} className="border-t-[3px] border-primary pt-7">
                <div className="w-[96px] h-[96px] rounded-full bg-[#F2F2F3] flex items-center justify-center mb-5">
                  <SiteImage src={`${ASSETS}/${v.img}`} alt="" className="w-[92px] h-[92px] block" />
                </div>
                <h3 className="m-0 text-[22px] font-extrabold tracking-[-0.02em] text-[#1A1A1A]">{v.title}</h3>
                <p className="mt-[12px] text-[16.5px] leading-[1.55] text-[#5a5a5c] text-pretty">{v.desc}</p>
              </FadeUp>
            ))}
          </div>
          {/* Proof — value/Why Reframe, light */}
          <FadeUp delay={0.4} className="mt-[52px] max-w-[600px] mx-auto">
            <StoryReview review={WLD_STORY_REVIEWS[7]} variant="light" context="proof" />
          </FadeUp>
          <FadeUp delay={0.3} className="flex justify-center mt-[32px]">
            <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[24px] py-[21px] rounded-[10px]" asChild>
              <Link href="/shop">
                Buy Curriculum
              </Link>
            </Button>
          </FadeUp>
        </div>
      </section>

      {/* GUIDE — proof [3] dark */}
      <section className="bg-[#1A1A1A] text-white overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-10 pt-[100px]">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-[48px] items-center">
            <FadeUp>
              <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[20px]">You've got a partner in this</div>
              <h2 className="m-0 text-[clamp(32px,3.3vw,46px)] leading-[1.07] tracking-[-0.03em] font-black text-white text-balance">
                We know the chaos and the calling of student ministry, because we <CircleWord>live it.</CircleWord>
              </h2>
              <p className="mt-[22px] text-[18px] leading-[1.6] text-white/70 text-pretty">
                Reframe was built by a licensed counselor and youth pastors who got tired of curriculum that didn't fit real students. Every series is shaped by what teens and leaders actually told us they need.
              </p>
              {/* Proof [3] — guide/partner, dark */}
              <FadeUp delay={0.2} className="mt-[28px] max-w-[520px]">
                <StoryReview review={HOME_STORY_REVIEWS[3]} variant="dark" context="editorial" />
              </FadeUp>
            </FadeUp>
            <FadeUp delay={0.15} className="relative overflow-hidden rounded-[16px]">
              <div className="relative flex w-max animate-marquee">
                {[1, 2, 3].map((group) => (
                  <div key={group} className="flex gap-[18px] pr-[18px]">
                    {["jeff.jpg", "will.jpg", "team-3.jpg", "jesse.jpg", "action.png"].map((img, j) => (
                      <div key={j} className="relative w-[210px] h-[270px] rounded-[16px] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.3)] border border-white/10 shrink-0 group">
                        <SiteImage
                          src={`${BASE_URL}site/team/${img.replace(/\.(jpg|png)$/, "")}-210.webp`}
                          srcSet={`${BASE_URL}site/team/${img.replace(/\.(jpg|png)$/, "")}-210.webp 210w, ${BASE_URL}site/team/${img.replace(/\.(jpg|png)$/, "")}-420.webp 420w`}
                          sizes="210px"
                          alt="Reframe team member"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent opacity-60" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="pointer-events-none absolute inset-y-0 left-0 w-[60px] bg-gradient-to-r from-[#1A1A1A] to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-[60px] bg-gradient-to-l from-[#1A1A1A] to-transparent" />
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[20px] mt-[56px] pb-[104px] items-stretch">
            <FadeUp delay={0.1} className="rounded-[16px] border border-white/10 bg-white/[0.04] p-[32px] flex flex-col justify-center">
              <div className="border-l-[3px] border-primary pl-6">
                <p className="m-0 text-[16.5px] leading-[1.6] font-medium text-white text-pretty">
                  We come from urban youth ministry, and we're proud of it. Culture is born in the city and bleeds outward, so curriculum built at the source connects wherever you lead, from the block to the suburbs to the small town.
                </p>
                <p className="mt-[12px] text-[16.5px] leading-[1.6] font-extrabold text-primary">
                  This isn't a smaller market. It's where culture starts.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2} className="rounded-[16px] border border-white/10 bg-white/[0.04] p-[32px]">
              <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[16px]">We did the research</div>
              <p className="m-0 text-[17px] leading-[1.6] text-white/70 text-pretty">
                This isn't guesswork. We went straight to the source, surveying students and youth leaders about what's actually happening in their heads, their homes, and their ministries.
              </p>
              <div className="grid grid-cols-2 gap-[16px] mt-[28px]">
                <div className="rounded-[16px] bg-[#1A1A1A] border border-white/10 px-[24px] py-[22px] text-center">
                  <div className="text-[38px] font-black tracking-[-0.035em] text-white leading-none">424</div>
                  <div className="mt-[8px] text-[12px] font-extrabold tracking-[0.13em] uppercase text-white/55">Students surveyed</div>
                </div>
                <div className="rounded-[16px] bg-[#1A1A1A] border border-white/10 px-[24px] py-[22px] text-center">
                  <div className="text-[38px] font-black tracking-[-0.035em] text-white leading-none">128</div>
                  <div className="mt-[8px] text-[12px] font-extrabold tracking-[0.13em] uppercase text-white/55">Youth leaders surveyed</div>
                </div>
              </div>
              <div className="mt-[32px]">
                <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[24px] py-[21px] rounded-[10px]" asChild>
              <Link href="/shop">
                Buy Curriculum
              </Link>
            </Button>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TRUSTED BY YOUTH LEADERS — proof [4] + VideoReviewsSection embedded */}
      <section className="relative overflow-hidden">
        <ResponsiveBackground
          desktopSrc={`${BASE_URL}site/social-proof-back-1440.webp`}
          desktopSrcSet={`${BASE_URL}site/social-proof-back-1440.webp 1440w, ${BASE_URL}site/social-proof-back-1920.webp 1920w`}
          mobileSrc={`${BASE_URL}site/social-proof-back-mobile-804.webp`}
        />
        <div className="relative max-w-[1240px] mx-auto px-10 pt-[96px] pb-[100px]">
          <FadeUp className="max-w-[720px] mx-auto text-center">
            <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[18px]">Reviews</div>
            <h2 className="m-0 text-[clamp(32px,3.3vw,48px)] leading-[1.06] tracking-[-0.03em] font-black text-[#1A1A1A]">
              Trusted by youth leaders
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-[#1A1A1A]/10 border border-[#1A1A1A]/10 rounded-[16px] overflow-hidden mt-[54px]">
            {[
              { num: "64+", label: "Ready-to-teach series" },
              { num: "4.9", star: true, label: "Average leader rating" },
              { num: "1,000s", label: "Of students reached" }
            ].map((s, i) => (
              <FadeUp key={i} delay={0.1 * i} className="bg-[#1A1A1A] p-7 text-center">
                <div className="text-[42px] font-black tracking-[-0.03em] text-white leading-none">
                  {s.num}{s.star && <span className="text-primary">★</span>}
                </div>
                <div className="mt-2 text-[12.5px] font-bold tracking-[0.13em] uppercase text-white/55">{s.label}</div>
              </FadeUp>
            ))}
          </div>
          {/* Proof — Trusted by youth leaders */}
          <FadeUp delay={0.3} className="mt-[48px] max-w-[600px] mx-auto">
            <StoryReview
              review={WLD_STORY_REVIEWS[9]}
              variant="dark"
              context="proof"
              className="rounded-[16px] bg-[#1A1A1A] p-[28px] sm:p-[32px] shadow-[0_16px_36px_rgba(26,26,26,0.22)]"
            />
          </FadeUp>
          {/* Video reviews embedded inside this section */}
          <VideoReviewsSection embedded={true} className="mt-[64px]" />
        </div>
      </section>

      {/* PLAN */}
      <section id="how-it-works" className="bg-white scroll-mt-24">
        <div className="max-w-[1240px] mx-auto px-10 pt-[100px] pb-[104px]">
          <FadeUp className="max-w-[720px] mx-auto text-center">
            <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[18px]">The plan</div>
            <h2 className="m-0 text-[clamp(32px,3.3vw,48px)] leading-[1.06] tracking-[-0.03em] font-black text-[#1A1A1A]">How Reframe works</h2>
            <p className="mt-[18px] text-[18.5px] leading-[1.55] text-[#5a5a5c] text-pretty">
              Three steps from "where do I start?" to a room full of students talking about their faith.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-[60px]">
            {[
              { t: "Pick your path", d: "Preorder WLD Vol. 3, take the Curriculum Quiz, or search the store for the exact topic your group needs." },
              { t: "Download & prep", d: "Instant access to teaching videos, small-group guides, games, devotionals. Everything's built." },
              { t: "Lead a dialogue", d: "Spark conversations that matter and build a faith that lasts, no all-nighter required." }
            ].map((p, i) => (
              <FadeUp key={i} delay={0.1 * i}>
                <div className="w-[52px] h-[52px] rounded-full bg-primary text-white text-[20px] font-black flex items-center justify-center">{i + 1}</div>
                <h3 className="mt-[22px] text-[23px] font-extrabold tracking-[-0.02em] text-[#1A1A1A]">{p.t}</h3>
                <p className="mt-[12px] text-[16.5px] leading-[1.55] text-[#5a5a5c] text-pretty">{p.d}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.4} className="flex justify-center mt-[32px]">
            <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[24px] py-[21px] rounded-[10px]" asChild>
              <Link href="/shop">
                Buy Curriculum
              </Link>
            </Button>
          </FadeUp>
        </div>
      </section>

      {/* PRODUCT & PRICE — proof [6] light */}
      <section className="bg-[#F2F2F3]">
        <div className="max-w-[1240px] mx-auto px-10 pt-[100px] pb-[104px]">
          <FadeUp className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-[60px] items-center bg-white rounded-[16px] overflow-hidden border border-[#1A1A1A]/5">
            <SiteImage src={`${ASSETS}/faqbg.jpg`} alt="Reframe series covers" className="w-full h-[540px] object-cover block" />
            <div className="p-10 lg:p-[56px_56px_56px_0]">
              <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-4">Whole Life Discipleship Vol. 3 · Presale</div>
              <h2 className="m-0 text-[clamp(30px,2.9vw,42px)] leading-[1.08] tracking-[-0.03em] font-black text-[#1A1A1A] text-balance">A full year of conversations that form disciples</h2>
              <p className="mt-[18px] text-[17.5px] leading-[1.55] text-[#5a5a5c] text-pretty">12 series across four phases that pair seven Core Values with the seven Signs of a Healthy Youth Ministry. Releasing January 1, 2027. Run it in sequence or drop in what you need.</p>
              <div className="flex flex-col gap-3 mt-[28px]">
                {[
                  { t: "12 series across four phases (sequence or drop-in)" },
                  { t: "Leader Training: The 7 Signs of a Healthy Youth Ministry", badge: true },
                  { t: "Built on 2025 research with 424 urban teens and 128 urban leaders", badge: true },
                  { t: "A 12-week middle-school-specific series", badge: true }
                ].map((l, i) => (
                  <div key={i} className="flex gap-[13px] items-start">
                    <span className="flex-none w-5 h-5 rounded-full bg-primary text-white text-[11px] font-black flex items-center justify-center mt-0.5">✓</span>
                    <span className="text-[16.5px] leading-[1.45] text-[#1A1A1A] font-semibold flex items-center gap-1.5 flex-wrap">
                      {l.t}
                      {l.badge && <SiteImage src={`${ASSETS}/newbadge.png`} alt="New" className="h-[13px] w-auto" />}
                    </span>
                  </div>
                ))}
              </div>
              {/* Proof — product/pricing */}
              <div className="mt-[28px]">
                <StoryReview review={WLD_STORY_REVIEWS[8]} variant="light" context="proof" />
              </div>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-[28px] mt-[28px] items-stretch">
            <FadeUp delay={0.1} className="bg-[#1A1A1A] border border-[#1A1A1A] rounded-[16px] overflow-hidden flex flex-col relative shadow-[0_18px_40px_rgba(26,26,26,0.16)]">
              <div className="absolute top-3.5 left-5 z-10 bg-primary text-white text-[10.5px] font-extrabold tracking-[0.13em] uppercase px-3 py-1.5 rounded-[8px]">Most popular</div>
              <SiteImage
                src={`${BASE_URL}site/bundle-tile-800.webp`}
                srcSet={`${BASE_URL}site/bundle-tile-480.webp 480w, ${BASE_URL}site/bundle-tile-800.webp 800w, ${BASE_URL}site/bundle-tile-1200.webp 1200w`}
                sizes="(min-width: 768px) 586px, calc(100vw - 80px)"
                width="1176"
                height="1470"
                loading="lazy"
                decoding="async"
                alt=""
                className="w-full aspect-video md:aspect-auto md:h-[240px] object-cover object-top block"
              />
              <div className="p-[30px_32px_32px] flex flex-col flex-1">
                <div className="text-[12.5px] font-extrabold tracking-[0.13em] uppercase text-white/55">Build a Bundle</div>
                <div className="mt-3.5 text-[46px] font-black tracking-[-0.035em] text-white leading-none">4 · 8 · 12</div>
                <p className="mt-[14px] text-[16.5px] leading-[1.5] text-white/70 flex-1">Pick exactly what your calendar needs.</p>
                <Button className="mt-[26px] bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[12.5px] tracking-[0.09em] uppercase py-[18px] rounded-[10px]" asChild>
                  <Link href="/build-bundle">Build Your Bundle</Link>
                </Button>
              </div>
            </FadeUp>

            <FadeUp delay={0.2} className="bg-white border border-[#1A1A1A]/5 rounded-[16px] overflow-hidden flex flex-col">
              <SiteImage
                src={`${BASE_URL}site/wld-vol3-bundle-800.webp`}
                srcSet={`${BASE_URL}site/wld-vol3-bundle-480.webp 480w, ${BASE_URL}site/wld-vol3-bundle-800.webp 800w, ${BASE_URL}site/wld-vol3-bundle-1200.webp 1200w`}
                sizes="(min-width: 768px) 586px, calc(100vw - 80px)"
                width="1200"
                height="675"
                loading="lazy"
                decoding="async"
                alt="WLD Vol. 3 curriculum"
                className="w-full aspect-video md:aspect-auto md:h-[240px] object-cover object-center block"
              />
              <div className="p-[30px_32px_32px] flex flex-col flex-1">
                <div className="text-[12.5px] font-extrabold tracking-[0.13em] uppercase text-[#666669]">WLD Vol. 3 · Presale</div>
                <div className="mt-3.5 text-[46px] font-black tracking-[-0.035em] text-[#1A1A1A] leading-none">Full Year</div>
                <p className="mt-[14px] text-[16.5px] leading-[1.5] text-[#5a5a5c] flex-1">The complete year of discipleship. Releases January 1, 2027.</p>
                <Button variant="outline" className="mt-[26px] border-[1.5px] border-[#1A1A1A]/20 text-[#1A1A1A] font-extrabold text-[12.5px] tracking-[0.09em] uppercase py-[18px] rounded-[10px] hover:border-[#1A1A1A] hover:bg-transparent" asChild>
                  <Link href="/wld">Preorder WLD Vol. 3</Link>
                </Button>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TRANSFORMATION — proof [7] light */}
      <section id="transformation" className="relative overflow-hidden scroll-mt-24">
        <ResponsiveBackground
          desktopSrc={`${BASE_URL}site/transformation-desktop-1440.webp`}
          desktopSrcSet={`${BASE_URL}site/transformation-desktop-1440.webp 1440w, ${BASE_URL}site/transformation-desktop-1920.webp 1920w`}
          mobileSrc={`${BASE_URL}site/transformation-mobile-804.webp`}
        />
        <div className="relative max-w-[1240px] mx-auto px-10 pt-[100px] pb-[104px]">
          <FadeUp className="max-w-[900px] mx-auto text-center">
            <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-white/80 mb-[18px]">What changes</div>
            <p className="m-0 text-[clamp(24px,2.5vw,34px)] leading-[1.32] tracking-[-0.02em] font-bold text-white text-pretty">
              Imagine a ministry where tough topics become gospel conversations, students show up ready to talk, and parents can actually follow through at home.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-[32px] items-center mt-[60px]">
            <FadeUp delay={0.1} className="bg-white rounded-[16px] p-[38px_36px] shadow-[0_16px_40px_rgba(0,0,0,0.2)]">
              <div className="text-[12px] font-extrabold tracking-[0.15em] uppercase text-[#666669] mb-[14px]">From</div>
              <p className="m-0 text-[18px] leading-[1.55] text-[#5a5a5c] text-pretty">
                Piecing together lessons that don't land. Devoted, but stretched thin and unsure it's making a lasting difference.
              </p>
            </FadeUp>
            <FadeUp delay={0.2} className="text-[30px] text-white font-black text-center hidden lg:block">→</FadeUp>
            <FadeUp delay={0.3} className="bg-[#1A1A1A] rounded-[16px] p-[38px_36px] shadow-[0_16px_40px_rgba(0,0,0,0.3)]">
              <div className="text-[12px] font-extrabold tracking-[0.15em] uppercase text-primary mb-[14px]">To</div>
              <p className="m-0 text-[18px] leading-[1.55] text-white font-medium text-pretty">
                A confident, resourced leader guiding students into a real, lasting faith, and raising up the next ones to lead.
              </p>
            </FadeUp>
          </div>
          {/* Proof [7] — transformation */}
          <FadeUp delay={0.4} className="mt-[48px] max-w-[600px] mx-auto text-center">
            <StoryReview
              review={HOME_STORY_REVIEWS[7]}
              variant="dark"
              context="proof"
              accent="white"
              className="text-center [&>div]:justify-center [&>div>div]:text-white"
            />
          </FadeUp>
        </div>
      </section>

      {/* WHAT'S AT STAKE — proof [8] dark */}
      <section id="whats-at-stake" className="bg-[#1A1A1A] scroll-mt-24">
        <FadeUp className="max-w-[900px] mx-auto px-10 pt-[88px] pb-[92px] text-center">
          <div className="text-[11.5px] font-extrabold tracking-[0.17em] uppercase text-primary mb-[18px]">The conversation starts here</div>
          <h2 className="m-0 text-[clamp(34px,4.2vw,60px)] leading-[1.02] tracking-[-0.04em] font-black text-white text-balance">
            The Questions Aren’t Going Away. <span className="text-primary">So Let’s Have the Conversation.</span>
          </h2>
          <p className="mt-[28px] text-[19px] leading-[1.58] text-white/85 text-pretty">
            Your students are already wrestling with questions about identity, anxiety, relationships, culture, doubt, and faith. You don’t have to have every answer, you just need to be willing to start the conversation.
          </p>
          <p className="mt-[22px] text-[18.5px] leading-[1.58] text-white/70 text-pretty">
            Imagine your youth ministry being the place in your community where students feel safe to wrestle honestly, encounter biblical truth, and grow deeper in their relationship with Jesus.
          </p>
          <div
            aria-label="Real questions lead to honest conversations, biblical truth, and deeper faith"
            className="mt-[48px] flex flex-wrap items-center justify-center gap-x-[12px] gap-y-3"
          >
            {[
              "Real questions",
              "Honest conversations",
              "Biblical truth",
              "Deeper faith",
            ].map((step, i) => (
              <React.Fragment key={step}>
                <span className={`rounded-full border px-[18px] py-3 text-[13px] font-extrabold tracking-[0.08em] uppercase ${i === 3 ? "border-primary bg-primary text-white" : "border-white/30 bg-[#1A1A1A] text-white"}`}>
                  {step}
                </span>
                {i < 3 && <span aria-hidden="true" className="text-primary text-[22px] font-black leading-none">→</span>}
              </React.Fragment>
            ))}
          </div>
          {/* Proof — failure/stakes, dark */}
          <div className="mt-[40px] max-w-[560px] mx-auto text-center">
            <StoryReview
              review={HOME_STORY_REVIEWS[9]}
              variant="dark"
              context="proof"
              className="[&>div]:justify-center"
            />
          </div>
          <div className="flex justify-center mt-[36px]">
            <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[24px] py-[21px] rounded-[10px]" asChild>
              <Link href="/shop">
                Buy Curriculum
              </Link>
            </Button>
          </div>
        </FadeUp>
      </section>

      {/* LEAD GEN */}
      <section id="leadgen" className="bg-[#F2F2F3]">
        <div className="max-w-[1240px] mx-auto px-10 pt-[96px] pb-[100px]">
          <FadeUp className="max-w-[700px] mx-auto text-center">
            <h2 className="m-0 text-[clamp(30px,3vw,44px)] leading-[1.07] tracking-[-0.03em] font-black text-[#1A1A1A]">
              Not ready to buy? <ScribbleUnder thick>Start here.</ScribbleUnder>
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-[48px]">
            <FadeUp delay={0.1} className="relative rounded-[16px] overflow-hidden bg-primary flex flex-col p-[44px_42px]">
              <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${publicAsset(`${ASSETS}/quizbg.jpg`)})` }} />
              <div className="absolute inset-0 bg-gradient-to-b from-[#b4192a]/40 to-[#b4192a]/70" />
              <div className="relative flex flex-col flex-1">
                <h3 className="m-0 text-[26px] font-extrabold tracking-[-0.02em] text-white">Take the Curriculum Quiz</h3>
                <p className="mt-[14px] text-[17px] leading-[1.55] text-white/90 flex-1 text-pretty">Find your best starting point in 60 seconds, matched to your students and calendar.</p>
                <Button className="self-start mt-[28px] bg-white text-primary hover:bg-[#1A1A1A] hover:text-white font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-[26px] py-[20px] rounded-[10px] transition-colors" asChild>
                  <Link href="/curriculum-quiz">Take the Quiz <ArrowRight className="w-3.5 h-3.5 ml-1.5 translate-y-[1px]" /></Link>
                </Button>
              </div>
            </FadeUp>
            <FadeUp delay={0.2} className="bg-white rounded-[16px] border border-[#1A1A1A]/5 p-[44px_42px] flex flex-col">
              <h3 className="m-0 text-[26px] font-extrabold tracking-[-0.02em] text-[#1A1A1A]">Truth in the Trenches</h3>
              <p className="mt-[14px] text-[17px] leading-[1.55] text-[#5a5a5c] flex-1 text-pretty">Field-tested ideas twice a month, plus the occasional product update. No fluff. Unsubscribe anytime.</p>
              {emailStatus === "success" ? (
                <p className="mt-[24px] text-[17px] font-bold text-primary">Thanks. We won't add this address until it has been verified.</p>
              ) : (
                <>
                  <form onSubmit={handleEmailSubmit} className="mt-[24px] flex flex-col sm:flex-row gap-[10px]">
                    <label htmlFor="home-newsletter-email" className="sr-only">Email address for the Truth in the Trenches newsletter</label>
                    <Input id="home-newsletter-email" type="email" name="email" placeholder="you@ministry.org" className="flex-1 py-6 px-[18px] text-[16px] rounded-[10px] border-[1.5px] border-[#1A1A1A]/15 focus-visible:ring-0 focus-visible:border-primary shadow-none" required disabled={emailStatus === "submitting"} />
                    <Button type="submit" disabled={emailStatus === "submitting"} className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13px] tracking-[0.09em] uppercase px-[24px] py-[18px] rounded-[10px]">
                      {emailStatus === "submitting" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
                    </Button>
                  </form>
                  {emailStatus === "error" && (
                    <p className="mt-[10px] text-[14px] font-semibold text-red-600">Something went wrong. Please try again.</p>
                  )}
                </>
              )}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[760px] mx-auto px-10 pt-[96px] pb-[100px]">
          <FadeUp>
            <h2 className="m-0 text-[clamp(30px,3vw,44px)] leading-[1.07] tracking-[-0.03em] font-black text-[#1A1A1A] text-center mb-[48px]">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="w-full space-y-3"
              onValueChange={(value) => {
                if (value) setVisitedFaqs((previous) => new Set([...previous, value]));
              }}>
              {[
                { q: "What's your statement of faith?", a: <>We hold to historic, orthodox Christian orthodoxy. For a detailed breakdown of our theological positions, please visit our <Link href="/statement-of-faith" className="underline font-semibold text-[#1A1A1A]">Statement of Faith</Link> page.</> },
                { q: "What's included in each curriculum?", a: "Every series includes a comprehensive leader guide, small-group discussion guides, a bumper video, teaching videos, games, social media assets, and parent emails." },
                { q: "Is Reframe tied to a specific denomination?", a: "No. Reframe is used by ministries across multiple denominations. We focus on core biblical truths and leave secondary denominational distinctives to you." },
                { q: "How do I access resources after purchase?", a: "Immediately upon purchase, you'll receive a secure download link via email containing all your digital assets in organized folders." }
              ].map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-[#F2F2F3] border-none rounded-[16px] px-6 py-1">
                  <AccordionTrigger className="text-[17px] font-bold text-[#1A1A1A] hover:no-underline text-left">{faq.q}</AccordionTrigger>
                  {visitedFaqs.has(`item-${i}`) && <AccordionContent className="text-[16px] leading-[1.55] text-[#5a5a5c] pb-5">
                    {faq.a}
                  </AccordionContent>}
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>

      {/* FINAL CTA — proof [9] dark */}
      <section className="relative bg-[#1A1A1A]">
        <ResponsiveBackground
          desktopSrc={`${BASE_URL}site/cta-bg-1440.webp`}
          desktopSrcSet={`${BASE_URL}site/cta-bg-768.webp 768w, ${BASE_URL}site/cta-bg-1440.webp 1440w, ${BASE_URL}site/cta-bg-1920.webp 1920w`}
        />
        <div className="absolute inset-0 bg-[#121212]/70" />
        <FadeUp className="relative max-w-[900px] mx-auto px-10 pt-[100px] pb-[104px] text-center">
          <div className="mb-[28px] flex justify-center">
            <TrustBadge />
          </div>
          <h2 className="m-0 text-[clamp(32px,3.4vw,50px)] leading-[1.05] tracking-[-0.035em] font-black text-white text-balance">
            Ready to lead a ministry students don't want to miss?
          </h2>
          <p className="mt-[22px] text-[19px] leading-[1.55] text-white/70 text-pretty">
            Browse the curriculum, or book a call and we'll help you find the right starting point.
          </p>
          <div className="flex flex-wrap gap-[14px] justify-center mt-[36px]">
            <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[27px] py-[21px] rounded-[10px]" asChild>
              <Link href="/shop">
                Buy Curriculum
              </Link>
            </Button>
            <Button variant="outline" className="bg-transparent text-white border-[1.5px] border-white/50 font-extrabold text-[13.5px] tracking-[0.09em] uppercase px-[27px] py-[21px] rounded-[10px] hover:border-white hover:bg-white/10 hover:text-white" asChild>
              <a href="https://calendly.com/d/cqcn-dy2-qb3/reframe-discovery-call" target="_blank" rel="noopener noreferrer">
                Book a Discovery Call
              </a>
            </Button>
          </div>
          {/* Proof [9] — final CTA, dark */}
          <div className="mt-[36px] max-w-[520px] mx-auto text-left">
            <StoryReview review={HOME_STORY_REVIEWS[1]} variant="dark" context="hero" />
          </div>
        </FadeUp>
      </section>

      </DeferredPublicSection>
      </main>
      <DeferredPublicSection enabled={deferSections} id="home-footer"><SiteFooter showTrustBadge={false} /></DeferredPublicSection>
    </div>
  );
}
